import React, { useState, useEffect, useMemo } from 'react';
import {
  Star,
  ThumbsUp,
  ThumbsDown,
  ArrowRightLeft,
  Loader2,
} from 'lucide-react';
import { getOrders } from '../utils/orders';
import '../styles/ProfileFeedbackTab.css';

function maskUsername(username) {
  if (!username || username === 'Anonymous') return '***';
  const u = String(username);
  if (u.length <= 3) return `${u[0] || '*'}***`;
  return `${u.slice(0, 3)}***`;
}

function countCompletedSellerOrders(orders) {
  if (!Array.isArray(orders)) return 0;
  return orders.filter(
    (o) =>
      o.escrow_status === 'released' ||
      o.status === 'completed'
  ).length;
}

const ProfileFeedbackTab = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [completedOrders, setCompletedOrders] = useState(0);
  const [feedbackScore, setFeedbackScore] = useState(100);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [ordersResult, reviewsRes, statsRes] = await Promise.allSettled([
          getOrders('seller', false),
          fetch(`/api/sellers/${userId}/reviews?limit=200`),
          fetch(`/api/sellers/${userId}/stats`),
        ]);

        if (cancelled) return;

        if (ordersResult.status === 'fulfilled' && ordersResult.value?.data) {
          setCompletedOrders(countCompletedSellerOrders(ordersResult.value.data));
        } else {
          setCompletedOrders(0);
        }

        if (reviewsRes.status === 'fulfilled' && reviewsRes.value.ok) {
          const json = await reviewsRes.value.json();
          setReviews(Array.isArray(json.data) ? json.data : []);
        } else {
          setReviews([]);
        }

        if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
          const json = await statsRes.value.json();
          const pct = json.rating_percentage;
          setFeedbackScore(
            typeof pct === 'number' && !Number.isNaN(pct) ? pct : 100
          );
        } else {
          setFeedbackScore(100);
        }
      } catch (e) {
        if (!cancelled) {
          setReviews([]);
          setCompletedOrders(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const positiveCount = useMemo(
    () => reviews.filter((r) => r.thumbs_up !== false).length,
    [reviews]
  );
  const negativeCount = useMemo(
    () => reviews.filter((r) => r.thumbs_up === false).length,
    [reviews]
  );

  const filteredReviews = useMemo(() => {
    if (filter === 'positive') return reviews.filter((r) => r.thumbs_up !== false);
    if (filter === 'negative') return reviews.filter((r) => r.thumbs_up === false);
    return reviews;
  }, [reviews, filter]);

  if (!userId) {
    return (
      <div className="profile-feedback">
        <p className="profile-feedback-muted">Sign in to see your feedback.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profile-feedback profile-feedback--loading">
        <Loader2 className="profile-feedback-spinner" size={28} aria-hidden />
        <span className="profile-feedback-muted">Loading feedback…</span>
      </div>
    );
  }

  return (
    <div className="profile-feedback">
      <div className="profile-feedback-stats" role="region" aria-label="Feedback summary">
        <div className="profile-feedback-stat">
          <ArrowRightLeft className="profile-feedback-stat-icon profile-feedback-stat-icon--orders" size={22} aria-hidden />
          <span className="profile-feedback-stat-label">Completed orders</span>
          <span className="profile-feedback-stat-value">{completedOrders}</span>
        </div>
        <div className="profile-feedback-stat">
          <ThumbsUp className="profile-feedback-stat-icon profile-feedback-stat-icon--positive" size={22} aria-hidden />
          <span className="profile-feedback-stat-label">Positive feedback</span>
          <span className="profile-feedback-stat-value">{positiveCount}</span>
        </div>
        <div className="profile-feedback-stat">
          <ThumbsDown className="profile-feedback-stat-icon profile-feedback-stat-icon--negative" size={22} aria-hidden />
          <span className="profile-feedback-stat-label">Negative feedback</span>
          <span className="profile-feedback-stat-value">{negativeCount}</span>
        </div>
        <div className="profile-feedback-stat">
          <Star className="profile-feedback-stat-icon profile-feedback-stat-icon--score" size={22} aria-hidden fill="currentColor" />
          <span className="profile-feedback-stat-label">Feedback score</span>
          <span className="profile-feedback-stat-value">
            {Math.round(feedbackScore)}%
          </span>
        </div>
      </div>

      <div className="profile-feedback-filters" role="tablist" aria-label="Filter feedback">
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'all'}
          className={`profile-feedback-filter${filter === 'all' ? ' is-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'positive'}
          className={`profile-feedback-filter${filter === 'positive' ? ' is-active' : ''}`}
          onClick={() => setFilter('positive')}
        >
          <ThumbsUp size={16} aria-hidden />
          Positive
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'negative'}
          className={`profile-feedback-filter${filter === 'negative' ? ' is-active' : ''}`}
          onClick={() => setFilter('negative')}
        >
          <ThumbsDown size={16} aria-hidden />
          Negative
        </button>
      </div>

      {filteredReviews.length === 0 ? (
        <div className="profile-feedback-empty">
          <p className="profile-feedback-muted">
            {reviews.length === 0
              ? 'No feedback yet. Buyers can leave feedback after an order is completed.'
              : 'No feedback matches this filter.'}
          </p>
        </div>
      ) : (
        <ul className="profile-feedback-list">
          {filteredReviews.map((review) => {
            const positive = review.thumbs_up !== false;
            return (
              <li key={review.id} className="profile-feedback-item">
                <div className="profile-feedback-item-top">
                  <div className="profile-feedback-item-left">
                    {positive ? (
                      <ThumbsUp className="profile-feedback-thumb profile-feedback-thumb--up" size={18} aria-hidden />
                    ) : (
                      <ThumbsDown className="profile-feedback-thumb profile-feedback-thumb--down" size={18} aria-hidden />
                    )}
                    <span className="profile-feedback-service">
                      {review.service_type} | {maskUsername(review.buyer)}
                    </span>
                  </div>
                  <time className="profile-feedback-time" dateTime={review.created_at}>
                    {review.time_ago}
                  </time>
                </div>
                {review.comment && review.comment !== 'No comment' && (
                  <p className="profile-feedback-comment">{review.comment}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProfileFeedbackTab;
